import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'

import routes from '../../routers'
import Layout from '../../components/Layout'
import shrinkPhoto from '../../utils/shrinkPhoto'
import { apiError } from '../../api/account'
import {
  addDrafts,
  batchPhotoUrl,
  cancelBatch,
  createBatch,
  discardDraft,
  getBatch,
  getBatches,
  mergeDrafts,
  splitDraft,
  startBatch,
  updateDraft,
  uploadBatchPhoto,
} from '../../api/bulkScan'

import { BookProgress } from '../../components/CookbookPromo'

import '../PurchaseBook/styles.css'
import styles from './ScanBox.module.css'

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

const MAX_PHOTOS = 200
const THUMBS_SHOWN = 24
const sameFile = (a, b) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified

// Collect photos first (choose more, from another folder, or drag them in), then upload
// them all and start reading. Each is shrunk on the phone and uploaded on its own, so a
// whole box never times out. Then Morsel reads them in the background.
const Upload = ({ onStarted }) => {
  const [files, setFiles] = useState([])
  const [dragging, setDragging] = useState(false)
  const [progress, setProgress] = useState(null)
  const [error, setError] = useState('')

  const thumbs = useMemo(
    () => files.slice(0, THUMBS_SHOWN).map((f) => URL.createObjectURL(f)),
    [files]
  )
  useEffect(() => () => thumbs.forEach((u) => URL.revokeObjectURL(u)), [thumbs])

  const add = (list) => {
    const images = Array.from(list || []).filter(
      (f) => /^image\//.test(f.type) || /\.(jpe?g|png|heic|heif|webp)$/i.test(f.name)
    )
    setError('')
    setFiles((current) => {
      const merged = [...current, ...images.filter((f) => !current.some((c) => sameFile(c, f)))]
      if (merged.length > MAX_PHOTOS)
        setError(`A batch can have up to ${MAX_PHOTOS} photos. Start another batch for the rest.`)
      return merged.slice(0, MAX_PHOTOS)
    })
  }

  const choose = (e) => {
    add(e.target.files)
    e.target.value = ''
  }

  const drop = (e) => {
    e.preventDefault()
    setDragging(false)
    add(e.dataTransfer?.files)
  }

  const start = async () => {
    setError('')
    try {
      const { data: batch } = await createBatch()
      let done = 0
      setProgress({ done, total: files.length })
      for (const [i, file] of files.entries()) {
        const small = await shrinkPhoto(file)
        await uploadBatchPhoto(batch.id, small, `photo-${i + 1}.jpg`)
        done += 1
        setProgress({ done, total: files.length })
      }
      await startBatch(batch.id)
      onStarted(batch.id)
    } catch (err) {
      setError(
        apiError(err, 'Something went wrong uploading. Check your connection and try again.')
      )
      setProgress(null)
    }
  }

  if (progress) {
    return (
      <section className="cbSection">
        <h2 className="cbSectionTitle">
          Uploading {progress.done} of {progress.total} photos…
        </h2>
        <div className={styles.bar}>
          <span style={{ width: `${(100 * progress.done) / progress.total}%` }} />
        </div>
        <p className="cbMuted">
          Keep this page open until the upload finishes. Reading carries on without you.
        </p>
      </section>
    )
  }
  return (
    <section
      className={`cbSection ${styles.upload} ${dragging ? styles.dragging : ''}`}
      onDragOver={(e) => {
        e.preventDefault()
        setDragging(true)
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false)
      }}
      onDrop={drop}
    >
      <h2 className="cbSectionTitle">Scan a whole recipe box</h2>
      <p className="cbHint">
        Add all your recipe card photos, from your camera roll or files. You can add more from
        another folder, or drag them onto this box. Several cards in one photo is fine, and so is
        the back of a card in the next photo. When they’re all here, we read them in the background
        and make a draft for each recipe for you to check.
      </p>

      {files.length ? (
        <div className={styles.staged}>
          {thumbs.map((url, i) => (
            <span key={url} className={styles.stagedThumb}>
              <img src={url} alt={`Photo ${i + 1}`} />
              <button
                type="button"
                aria-label={`Remove photo ${i + 1}`}
                onClick={() => setFiles(files.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </span>
          ))}
          {files.length > THUMBS_SHOWN ? (
            <span className={styles.more}>+{files.length - THUMBS_SHOWN} more</span>
          ) : null}
        </div>
      ) : (
        <div className={styles.dropZone}>Drop photos here</div>
      )}

      <div className={styles.uploadActions}>
        <label className={`${files.length ? 'cbSecondaryButton' : 'cbNextButton'} ${styles.pick}`}>
          {files.length ? 'Add more photos' : 'Choose photos'}
          <input type="file" accept="image/*" multiple onChange={choose} />
        </label>
        {files.length ? (
          <>
            <button type="button" className="cbNextButton" onClick={start}>
              Upload and read {plural(files.length, 'photo')}
            </button>
            <button type="button" className={styles.linkButton} onClick={() => setFiles([])}>
              Clear
            </button>
          </>
        ) : null}
      </div>
      {error ? <p className="cbError">{error}</p> : null}
    </section>
  )
}

const Draft = ({ batchId, draft, selected, onSelect, onChange }) => {
  const [name, setName] = useState(draft.name || '')
  const [open, setOpen] = useState(false)
  const [ingredients, setIngredients] = useState(draft.ingredients_text || '')
  const [steps, setSteps] = useState(draft.steps_text || '')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setName(draft.name || '')
    setIngredients(draft.ingredients_text || '')
    setSteps(draft.steps_text || '')
  }, [draft.name, draft.ingredients_text, draft.steps_text])

  const save = (params) => updateDraft(batchId, draft.id, params).then(({ data }) => onChange(data))
  const act = (fn) => {
    setBusy(true)
    fn()
      .then(({ data }) => onChange(data))
      .catch(() => null)
      .finally(() => setBusy(false))
  }

  if (draft.status === 'reading') {
    return (
      <article className={`${styles.draft} ${styles.reading}`}>
        <p className="cbMuted">Reading this photo again…</p>
      </article>
    )
  }
  const added = draft.status === 'added'
  return (
    <article
      className={`${styles.draft} ${selected ? styles.selected : ''} ${added ? styles.added : ''}`}
    >
      <div className={styles.draftTop}>
        {!added ? (
          <input
            type="checkbox"
            className={styles.check}
            checked={selected}
            onChange={(e) => onSelect(e.target.checked)}
            aria-label={`Choose ${name || 'this draft'}`}
          />
        ) : null}
        <div className={styles.thumbs}>
          {draft.photos.map((p, i) => (
            <a key={p} href={batchPhotoUrl(p)} target="_blank" rel="noopener noreferrer">
              <img src={batchPhotoUrl(p)} alt={`Photo ${i + 1}`} loading="lazy" />
            </a>
          ))}
        </div>
        <div className={styles.draftBody}>
          {added ? (
            <p className={styles.addedName}>
              {draft.name}{' '}
              <Link className="cbTextLink" to={`${routes.addRecipe}?id=${draft.recipe_id}`}>
                Open
              </Link>
            </p>
          ) : (
            <input
              className={`cbInput ${styles.name}`}
              value={name}
              placeholder="Recipe name"
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => name !== draft.name && save({ name })}
            />
          )}
          <p className="cbMuted">
            {plural(draft.ingredient_count, 'ingredient')}, {plural(draft.step_count, 'step')}
            {draft.photos.length > 1 ? `, ${draft.photos.length} photos` : ''}
          </p>
          {draft.problem ? <p className={styles.problem}>{draft.problem}</p> : null}
        </div>
      </div>
      {!added ? (
        <>
          <div className={styles.draftActions}>
            <button type="button" className={styles.linkButton} onClick={() => setOpen(!open)}>
              {open ? 'Hide ingredients and steps' : 'Check ingredients and steps'}
            </button>
            {draft.photos.length > 1 ? (
              <button
                type="button"
                className={styles.linkButton}
                disabled={busy}
                onClick={() => act(() => splitDraft(batchId, draft.id))}
              >
                Split into one per photo
              </button>
            ) : null}
            <button
              type="button"
              className={styles.linkButton}
              disabled={busy}
              onClick={() => act(() => discardDraft(batchId, draft.id))}
            >
              Discard
            </button>
          </div>
          {open ? (
            <div className={styles.edit}>
              <label>
                Ingredients (one per line)
                <textarea
                  className="cbInput"
                  rows={Math.min(12, Math.max(4, ingredients.split('\n').length + 1))}
                  value={ingredients}
                  onChange={(e) => setIngredients(e.target.value)}
                  onBlur={() =>
                    ingredients !== draft.ingredients_text &&
                    save({ ingredients_text: ingredients })
                  }
                />
              </label>
              <label>
                Steps (one per line)
                <textarea
                  className="cbInput"
                  rows={Math.min(12, Math.max(4, steps.split('\n').length + 1))}
                  value={steps}
                  onChange={(e) => setSteps(e.target.value)}
                  onBlur={() => steps !== draft.steps_text && save({ steps_text: steps })}
                />
              </label>
            </div>
          ) : null}
        </>
      ) : null}
    </article>
  )
}

const Batch = ({ id }) => {
  const navigate = useNavigate()
  const groups = useSelector((state) => state.groups?.data) || []
  const [batch, setBatch] = useState(null)
  const [selected, setSelected] = useState([])
  const [groupId, setGroupId] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)
  const [error, setError] = useState('')
  const timer = useRef(null)

  const load = () =>
    getBatch(id)
      .then(({ data }) => {
        setBatch(data)
        clearTimeout(timer.current)
        if (['uploading', 'reading'].includes(data.status)) timer.current = setTimeout(load, 3000)
      })
      .catch((e) => setError(apiError(e, 'We could not load this batch.')))

  useEffect(() => {
    load()
    return () => clearTimeout(timer.current)
  }, [id])
  useEffect(() => {
    if (!groupId && groups[0]?.id) setGroupId(String(groups[0].id))
  }, [groups])

  const changed = (data) => {
    setBatch(data)
    setSelected((s) => s.filter((x) => data.drafts.some((d) => d.id === x && d.status === 'draft')))
    if (['uploading', 'reading'].includes(data.status)) {
      clearTimeout(timer.current)
      timer.current = setTimeout(load, 3000)
    }
  }

  if (error && !batch) return <p className="cbError">{error}</p>
  if (!batch) return <p className="cbMuted">Loading…</p>

  const waiting = batch.drafts.filter((d) => d.status === 'draft')
  const reading = batch.status === 'reading' || batch.status === 'uploading'
  const allChosen = waiting.length > 0 && selected.length === waiting.length

  const join = () => {
    setBusy(true)
    mergeDrafts(id, selected)
      .then(({ data }) => {
        changed(data)
        setSelected([])
      })
      .catch((e) => setError(apiError(e, 'We could not join those drafts.')))
      .finally(() => setBusy(false))
  }

  const add = () => {
    setBusy(true)
    setError('')
    addDrafts(id, { draft_ids: selected, group_id: groupId ? Number(groupId) : null })
      .then(({ data }) => {
        changed(data.batch)
        setSelected([])
        setNotice(data)
      })
      .catch((e) => setError(apiError(e, 'We could not add those recipes.')))
      .finally(() => setBusy(false))
  }

  const throwAway = () => {
    if (!window.confirm('Throw away this batch and its photos? Recipes you already added stay.'))
      return
    cancelBatch(id).then(() => navigate(routes.scanBox))
  }

  return (
    <>
      <section className="cbSection">
        <div className="cbSectionHead">
          <div>
            <h2 className="cbSectionTitle">
              {reading
                ? `Reading ${batch.photos.read + batch.photos.failed} of ${
                    batch.photos.total
                  } photos…`
                : `${plural(batch.photos.total, 'photo')} read`}
            </h2>
            <p className="cbMuted">
              {plural(waiting.length, 'recipe')} to check
              {batch.photos.failed
                ? `, ${plural(batch.photos.failed, 'photo')} we couldn’t read`
                : ''}
            </p>
          </div>
          <button type="button" className={styles.linkButton} onClick={throwAway}>
            Throw away
          </button>
        </div>
        {reading ? (
          <div className={styles.bar}>
            <span
              style={{
                width: `${
                  (100 * (batch.photos.read + batch.photos.failed)) /
                  Math.max(batch.photos.total, 1)
                }%`,
              }}
            />
          </div>
        ) : null}
        {reading ? (
          <p className="cbMuted">
            You can leave this page; we’ll keep reading. Drafts appear below as we go.
          </p>
        ) : null}
        {batch.room !== null && batch.room !== undefined ? (
          <p className={styles.room}>
            {batch.room > 0
              ? `Your free plan has room for ${plural(batch.room, 'more recipe')}.`
              : 'Your free plan is full (10 recipes).'}{' '}
            <Link className="cbTextLink" to={`${routes.settings}#subscription`}>
              Upgrade to Pro
            </Link>{' '}
            to add them all. Cancel any time.
          </p>
        ) : null}
        {notice ? (
          <p className={styles.notice}>
            Added {plural(notice.published + notice.need_details, 'recipe')}
            {notice.need_details
              ? ` (${notice.need_details} in Draft recipes need a few details)`
              : ''}
            .
            {notice.over_limit
              ? ` ${plural(notice.over_limit, 'recipe')} didn’t fit on the free plan and ${
                  notice.over_limit === 1 ? 'is' : 'are'
                } still here.`
              : ''}
          </p>
        ) : null}
        {notice && groupId ? <BookProgress groupId={Number(groupId)} /> : null}
      </section>

      {waiting.length ? (
        <div className={styles.toolbar}>
          <label className={styles.selectAll}>
            <input
              type="checkbox"
              checked={allChosen}
              onChange={(e) => setSelected(e.target.checked ? waiting.map((d) => d.id) : [])}
            />
            {selected.length ? `${selected.length} chosen` : 'Choose all'}
          </label>
          <button
            type="button"
            className="cbSecondaryButton"
            disabled={busy || selected.length < 2}
            onClick={join}
          >
            Join into one recipe
          </button>
          <select
            className={`cbInput ${styles.group}`}
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            aria-label="Family group"
          >
            <option value="">Only my recipes</option>
            {groups.map((g) => (
              <option key={g.id} value={String(g.id)}>
                {g.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="cbNextButton"
            disabled={busy || !selected.length}
            onClick={add}
          >
            {busy ? 'One moment…' : `Add ${selected.length || ''} to Morsel`}
          </button>
        </div>
      ) : null}
      {error ? <p className="cbError">{error}</p> : null}

      <div className={styles.drafts}>
        {batch.drafts.map((d) => (
          <Draft
            key={d.id}
            batchId={id}
            draft={d}
            selected={selected.includes(d.id)}
            onSelect={(on) => setSelected((s) => (on ? [...s, d.id] : s.filter((x) => x !== d.id)))}
            onChange={changed}
          />
        ))}
      </div>
    </>
  )
}

const ScanBox = () => {
  const { batchId } = useParams()
  const navigate = useNavigate()
  const user = useSelector((state) => state.user?.data)
  const [batches, setBatches] = useState([])

  useEffect(() => {
    if (!batchId)
      getBatches()
        .then(({ data }) => setBatches(data || []))
        .catch(() => setBatches([]))
  }, [batchId])

  const enabled = !!user?.features?.bulk_scan
  return (
    <Layout title="Scan a recipe box">
      <div className={`cbPage ${styles.page}`}>
        <div className="cbIntro">
          <div>
            {batchId ? (
              <Link className="cbBack" to={routes.scanBox}>
                ← All batches
              </Link>
            ) : null}
            <h1 className="cbTitle">Scan a recipe box</h1>
          </div>
        </div>
        {!enabled ? (
          <section className="cbSection">
            <p className="cbHint">Scanning a whole box at once is coming soon.</p>
          </section>
        ) : batchId ? (
          <Batch id={Number(batchId)} />
        ) : (
          <>
            <Upload onStarted={(id) => navigate(`${routes.scanBox}/${id}`)} />
            {batches.length ? (
              <section className="cbSection">
                <h2 className="cbSectionTitle">Your batches</h2>
                {batches.map((b) => (
                  <Link key={b.id} to={`${routes.scanBox}/${b.id}`} className={styles.batchRow}>
                    <span>
                      <b>{plural(b.photos, 'photo')}</b>
                      <small>{moment(b.created_at).format('MMM D, h:mm a')}</small>
                    </span>
                    <span className={styles.pill}>
                      {b.status === 'ready'
                        ? b.drafts_waiting
                          ? `${plural(b.drafts_waiting, 'recipe')} to check`
                          : 'Done'
                        : 'Reading…'}
                    </span>
                  </Link>
                ))}
              </section>
            ) : null}
          </>
        )}
      </div>
    </Layout>
  )
}

export default ScanBox
